<template>
  <div
    class="base-input-file base-input-file__outer"
    :class="[className, { disabled, error, loading }]"
  >
    <div class="base-input-file__wrapper">
      <slot
        :id="inputFileId"
        :files
        name="label"
      >
        <label
          v-if="label"
          class="base-input-file__label"
          :for="id"
        >
          {{ label }}
        </label>
      </slot>

      <div class="base-input-file__list">
        <slot
          :id="inputFileId"
          :files
          name="fileList"
        >
          <div
            class="base-input-file__inner"
            :class="{ hidden: files && files.length === 1 }"
          >
            <slot
              :id="inputFileId"
              :files
              name="thumb"
            >
              <div class="base-input-file__thumb">
                <slot
                  :id="inputFileId"
                  :files
                  name="thumbContent"
                />
              </div>
            </slot>

            <input
              :id="inputFileId"
              ref="inputFileElement"
              class="base-input-file__element"
              :disabled="disabled || loading"
              :multiple
              :name
              :placeholder="placeholder ?? label"
              type="file"
              v-bind="attrs"
              @change="(event) => handleChange(event)"
            />

            <slot
              :id="inputFileId"
              :files
              name="prefix"
            />

            <div
              v-if="loading || isConverting"
              class="base-input-file__loading"
            >
              <slot
                :id="inputFileId"
                :files
                name="loader"
              >
                <BaseLoader2
                  class="base-input-file__loader"
                  :class="classLoader"
                  :size="50"
                />
              </slot>
            </div>

            <div
              v-if="(!files || multiple) && !loading && !isConverting"
              class="base-input-file__placeholder"
            >
              <slot
                :id="inputFileId"
                :files
                name="placeholder"
              >
                {{ placeholder }}
              </slot>
            </div>

            <slot
              :id="inputFileId"
              :files
              :handle-delete
              name="suffix"
            />
          </div>

          <BaseDraggable
            class="base-input-file__draggable"
            :disabled="!draggable"
            :model-value="files ? [...files] : []"
            tag="ul"
            @update:model-value="(updatedFiles) => onDraggableUpdateHandler(updatedFiles)"
          >
            <li
              v-for="item in files"
              :key="item.lastModified + item.name"
              class="base-input-file__list-item draggable"
              @touchcancel="handleTouchEnd"
              @touchend="handleTouchEnd"
              @touchmove="handleTouchMove"
              @touchstart="(e) => handleTouchStart(e, e.currentTarget)"
            >
              <slot
                :id="inputFileId"
                :file="item"
                :files
                name="fileItem"
              >
                <img
                  alt=""
                  class="base-input-file__list-image"
                  :src="useCreateFilePreview(item)"
                />

                <span class="base-input-file__list-size">{{ item.size }}</span>

                <span class="base-input-file__list-type">{{ item.type }}</span>

                <span class="base-input-file__list-name">{{ fileName ?? item.name }}</span>

                <slot
                  :id="inputFileId"
                  :file="item"
                  :files
                  :handle-delete
                  name="fileDescription"
                  :open-file-dialog="() => openFileDialog()"
                  :open-cropper="() => openCropper(item)"
                  :enable-cropping="enableCropping"
                  :is-image-file="isImageFile(item)"
                />

                <div class="base-input-file__info">
                  <!-- Add crop button for images -->
                  <BaseButton2
                    v-if="enableCropping && isImageFile(item) && showCropButton"
                    class="base-input-file__list-crop"
                    @click="() => openCropper(item)"
                  >
                    {{ cropButtonText || t("common.crop") }}
                  </BaseButton2>

                  <slot
                    :id="inputFileId"
                    :file="item"
                    :files
                    :handle-delete
                    name="deleteButton"
                  >
                    <BaseButton2
                      v-if="deleteButtonText"
                      class="base-input-file__list-delete"
                      @click="() => handleDelete(item.name)"
                    >
                      {{ deleteButtonText }}
                    </BaseButton2>
                  </slot>
                </div>
              </slot>
            </li>
          </BaseDraggable>
        </slot>
      </div>
    </div>

    <div
      v-if="error || slots.error || ((hint || slots.hint) && (!files || files?.length === 0))"
      class="base-input-file__messages"
    >
      <slot
        :id="inputFileId"
        :files
        name="error"
      >
        <p
          v-if="error"
          class="base-input-file__error"
        >
          {{ error }}
        </p>
      </slot>

      <slot
        :id="inputFileId"
        :files
        name="hint"
      >
        <p
          v-if="hint && (!files || files?.length === 0)"
          class="base-input-file__hint"
        >
          {{ hint }}
        </p>
      </slot>
    </div>

    <!-- Inline Cropper Overlay (только если showInlineCropper = true) -->
    <div
      v-if="showInlineCropper && showCropperState && currentCropFile"
      class="base-input-file__cropper-overlay"
      @click.self="closeCropper"
    >
      <div class="base-input-file__cropper-container">
        <div class="base-input-file__cropper-header">
          <h3 class="base-input-file__cropper-title">
            {{ cropModalTitle || t("common.cropImage") }}
          </h3>
          <BaseButton2
            class="base-input-file__cropper-close"
            @click="closeCropper"
          >
            ×
          </BaseButton2>
        </div>

        <div class="base-input-file__cropper-content">
          <BaseImageCropper
            :id="cropperModalId"
            ref="cropperRef"
            :file="currentCropFile"
            :aspect-ratio="cropAspectRatio"
            :output-options="cropOutputOptions"
            :circular-overlay="cropCircularOverlay"
            @close="closeCropper"
            @crop="handleCropResult"
            @error="handleCropError"
            @reset="resetToOriginal"
          />
        </div>
      </div>
    </div>

    <!-- Modal Cropper (только если showInlineCropper = false) -->
    <LazyBaseImageCropperModal
      v-if="!showInlineCropper && isModalOpen(cropperModalId) && currentCropFile"
      :id="cropperModalId"
      :file="currentCropFile"
      :title="cropModalTitle"
      :aspect-ratio="cropAspectRatio"
      :output-options="cropOutputOptions"
      :circular-overlay="cropCircularOverlay"
      @close="closeCropper"
      @crop="handleCropResult"
      @error="handleCropError"
      @reset="resetToOriginal"
    />
  </div>
</template>

<script lang="ts" setup>
import { heicTo } from "heic-to";
import type { HTMLAttributes, InputHTMLAttributes } from "vue";
import type { CropResult } from "../stores/useImageCropper";

type Files = FileList | null;

interface Props extends /* @vue-ignore */ InputHTMLAttributes {
  class?: InputHTMLAttributes["class"];
  classLoader?: HTMLAttributes["class"];
  deleteButtonText?: string;
  disabled?: boolean;
  draggable?: boolean;
  error?: string;
  fileName?: string;
  hideFileList?: boolean;
  hint?: string;
  id?: InputHTMLAttributes["id"];
  label?: string;
  loading?: boolean;
  modelValue?: Files;
  multiple?: boolean;
  name: InputHTMLAttributes["name"];
  placeholder?: InputHTMLAttributes["placeholder"];
  // Cropping props
  enableCropping?: boolean;
  cropButtonText?: string;
  cropModalTitle?: string;
  cropAspectRatio?: number;
  cropOutputOptions?: {
    width?: number;
    height?: number;
    quality?: number;
    mimeType?: string;
  };
  cropAutoOpen?: boolean;
  showCropButton?: boolean;
  cropCircularOverlay?: boolean;
  showInlineCropper?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  enableCropping: false,
  cropAspectRatio: 1,
  cropOutputOptions: () => ({}),
  cropAutoOpen: false,
  showCropButton: true,
  cropCircularOverlay: false,
  showInlineCropper: false,
});

const emits = defineEmits<{
  clear: [];
  delete: [];
  "update:modelValue": [value: File[]];
  crop: [originalFile: File, croppedResult: CropResult];
  "crop-error": [error: string];
}>();

defineOptions({
  inheritAttrs: false,
});

const { t } = useI18n();
const { openModal, closeModal, isModalOpen } = useModalStore();
const { isImageFile } = useImageCropper();

const attrs = useAttrs();
const slots = useSlots();
const { class: className, id, modelValue, multiple, name, showInlineCropper } = toRefs(props);
const inputFileElement = ref<HTMLInputElement | null>(null);
const inputFileId = id.value ?? name.value;
const files = ref<Files>(null);

// Cropping state
const showCropperState = ref(false); // Состояние для встроенного кроппера
const currentCropFile = ref<File | null>(null);
const scrollPosition = ref(0); // Сохраняем позицию скролла
const cropperModalId = `${inputFileId}-cropper-modal`;

// Локальный стейт для индикации конвертации HEIC
const isConverting = ref(false);

// Храним оригинальные файлы для возможности повторного кадрирования
const originalFiles = ref<Map<string, File>>(new Map());

// Генерируем уникальный ключ для файла
const generateFileKey = (file: File): string => {
  return `${file.name}-${file.size}-${file.lastModified}`;
};

// Получаем оригинальное имя файла (убираем префикс cropped-)
const getOriginalFileName = (fileName: string): string => {
  return fileName.startsWith("cropped-") ? fileName.replace("cropped-", "") : fileName;
};

const updateFileInputValue = (fileList?: Files) => {
  if (fileList === undefined && inputFileElement.value) {
    inputFileElement.value.value = "";
  }

  emits("update:modelValue", fileList ?? null);
};

const handleChange = async (event: Event) => {
  const inputFile = event.target as HTMLInputElement;

  if (multiple.value && inputFile.files) {
    const newFiles = [...(files.value?.length ? files.value : []), ...inputFile.files];
    const uniqueFiles = generateUniqueFiles(newFiles);

    // Проверяем есть ли HEIC файлы
    const hasHeicFiles = uniqueFiles.some(file =>
      file.type === "image/heic" || file.type === "image/heif" ||
      file.name.toLowerCase().endsWith(".heic") || file.name.toLowerCase().endsWith(".heif")
    );

    if (hasHeicFiles) {
      isConverting.value = true;
    }

    try {
      // Конвертируем HEIC файлы в JPEG и сжимаем все изображения
      const convertedFiles = await Promise.all(
        uniqueFiles.map(async (file) => {
          // Проверяем если это HEIC
          if (file.type === "image/heic" || file.type === "image/heif" || file.name.toLowerCase().endsWith(".heic") || file.name.toLowerCase().endsWith(".heif")) {
            try {
              const convertedFile = await convertHeicToJpeg(file);
              return convertedFile;
            } catch (err) {
              console.error("Failed to auto-convert HEIC:", err);
              // Если конвертация не удалась, возвращаем оригинальный файл
              return file;
            }
          }

          // Сжимаем обычные изображения (JPEG, PNG и т.д.)
          if (file.type.startsWith("image/")) {
            try {
              const compressedFile = await compressImage(file, 2);
              return compressedFile;
            } catch (err) {
              console.error("Failed to compress image:", err);
              // Если сжатие не удалось, возвращаем оригинальный файл
              return file;
            }
          }

          return file;
        }),
      );

      // Сохраняем оригинальные/конвертированные файлы
      convertedFiles.forEach((file) => {
        const fileKey = generateFileKey(file);
        if (!originalFiles.value.has(fileKey)) {
          originalFiles.value.set(fileKey, file);
        }
      });

      const fileList = createFileList(convertedFiles);
      updateFileInputValue(fileList);
    } finally {
      isConverting.value = false;
    }
  }

  if (!multiple.value && inputFile.files) {
    let fileToSave = inputFile.files[0];

    if (fileToSave) {
      // Конвертируем HEIC в JPEG если нужно
      if (fileToSave.type === "image/heic" || fileToSave.type === "image/heif" || fileToSave.name.toLowerCase().endsWith(".heic") || fileToSave.name.toLowerCase().endsWith(".heif")) {
        isConverting.value = true;
        try {
          fileToSave = await convertHeicToJpeg(fileToSave);
        } catch (err) {
          console.error("Failed to auto-convert single HEIC:", err);
          // Продолжаем с оригинальным файлом
        } finally {
          isConverting.value = false;
        }
      } else if (fileToSave.type.startsWith("image/")) {
        // Сжимаем обычные изображения
        isConverting.value = true;
        try {
          fileToSave = await compressImage(fileToSave, 2);
        } catch (err) {
          console.error("Failed to compress single image:", err);
          // Продолжаем с оригинальным файлом
        } finally {
          isConverting.value = false;
        }
      }

      // Сохраняем файл
      const fileKey = generateFileKey(fileToSave);
      originalFiles.value.set(fileKey, fileToSave);

      const fileList = createFileList([fileToSave]);
      updateFileInputValue(fileList);

      if (props.cropAutoOpen) {
        openCropper(fileToSave);
      }
    }
  }

  if (!inputFile.value) {
    updateFileInputValue(modelValue.value);
  }
};

const handleClear = () => {
  if (inputFileElement.value) {
    originalFiles.value.clear();
    updateFileInputValue();
    emits("clear");
  }
};

const handleDelete = (fileName: string) => {
  if (inputFileElement.value && files.value && files.value.length > 0) {
    const filteredFiles = [...files.value].filter((file) => {
      return file.name !== fileName;
    });

    // Удаляем из оригинальных файлов
    const fileToDelete = [...files.value].find((file) => file.name === fileName);
    if (fileToDelete) {
      // Получаем оригинальное имя файла
      const originalName = getOriginalFileName(fileToDelete.name);

      // Ищем и удаляем оригинальный файл из карты
      for (const [key, origFile] of originalFiles.value.entries()) {
        if (origFile.name === originalName) {
          originalFiles.value.delete(key);
          break;
        }
      }
    }

    if (filteredFiles.length > 0) {
      const fileList = createFileList(filteredFiles);
      updateFileInputValue(fileList);
    } else {
      updateFileInputValue();
    }

    emits("delete");
  }
};

const onDraggableUpdateHandler = (newFiles: File[]) => {
  const fileList = createFileList(newFiles);
  updateFileInputValue(fileList);
};

const openFileDialog = () => {
  if (inputFileElement.value === null) {
    return;
  }

  inputFileElement.value.click();
};

// Дополнительное сжатие изображения если оно слишком большое
const compressImage = async (file: File, maxSizeMB: number = 2): Promise<File> => {
  const maxSizeBytes = maxSizeMB * 1024 * 1024;

  // Если файл уже меньше лимита, возвращаем как есть
  if (file.size <= maxSizeBytes) {
    return file;
  }

  return new Promise((resolve, reject) => {
    const img = new Image();
    const reader = new FileReader();

    reader.onload = (e) => {
      img.src = e.target?.result as string;
    };

    img.onload = () => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");

      if (!ctx) {
        reject(new Error("Cannot get canvas context"));
        return;
      }

      // Уменьшаем размеры если изображение очень большое
      let width = img.width;
      let height = img.height;
      const maxDimension = 2048; // Максимальная сторона

      if (width > maxDimension || height > maxDimension) {
        if (width > height) {
          height = (height * maxDimension) / width;
          width = maxDimension;
        } else {
          width = (width * maxDimension) / height;
          height = maxDimension;
        }
      }

      canvas.width = width;
      canvas.height = height;
      ctx.drawImage(img, 0, 0, width, height);

      // Пробуем разные уровни качества
      let quality = 0.85;
      const tryCompress = () => {
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error("Failed to compress image"));
              return;
            }

            // Если все еще больше лимита и можно еще сжать
            if (blob.size > maxSizeBytes && quality > 0.5) {
              quality -= 0.1;
              tryCompress();
            } else {
              const compressedFile = new File([blob], file.name, { type: "image/jpeg" });
              resolve(compressedFile);
            }
          },
          "image/jpeg",
          quality
        );
      };

      tryCompress();
    };

    img.onerror = () => reject(new Error("Failed to load image"));
    reader.onerror = () => reject(new Error("Failed to read file"));
    reader.readAsDataURL(file);
  });
};

// Конвертация HEIC в JPEG
const convertHeicToJpeg = async (file: File): Promise<File> => {
  try {
    let convertedBlob = await heicTo({
      blob: file,
      type: "image/jpeg",
      quality: 0.8,
    });

    let convertedFile = new File([convertedBlob], file.name.replace(/\.(heic|heif)$/i, ".jpg"), { type: "image/jpeg" });

    // Дополнительное сжатие если файл слишком большой
    convertedFile = await compressImage(convertedFile, 2);

    return convertedFile;
  } catch (err: any) {
    console.error("Failed to convert HEIC in BaseInputFile2:", err);
    throw err;
  }
};

// Cropping methods
const openCropper = async (file: File) => {
  if (!isImageFile(file) || !props.enableCropping) {
    return;
  }

  // Ищем оригинальный файл
  let originalFile = file;
  const originalName = getOriginalFileName(file.name);

  // Ищем оригинальный файл в карте
  for (const [key, origFile] of originalFiles.value.entries()) {
    if (origFile.name === originalName) {
      originalFile = origFile;
      break;
    }
  }

  // Если оригинальный файл не найден, но текущий файл не обрезанный, используем его как оригинал
  if (originalFile === file && !file.name.startsWith("cropped-")) {
    const fileKey = generateFileKey(file);
    originalFiles.value.set(fileKey, file);
  }

  // Конвертируем HEIC в JPEG перед открытием кропера
  if (originalFile.type === "image/heic" || originalFile.type === "image/heif" || originalFile.name.toLowerCase().endsWith(".heic") || originalFile.name.toLowerCase().endsWith(".heif")) {
    try {
      const convertedFile = await convertHeicToJpeg(originalFile);

      // Заменяем HEIC файл на JPEG в списке файлов
      if (files.value) {
        const fileArray = [...files.value];
        const fileIndex = fileArray.findIndex(f => f.name === originalFile.name);
        if (fileIndex !== -1) {
          fileArray[fileIndex] = convertedFile;
          const fileList = createFileList(fileArray);
          updateFileInputValue(fileList);
        }
      }

      // Обновляем originalFile на сконвертированный
      originalFile = convertedFile;

      // Сохраняем сконвертированный файл как оригинал
      const convertedFileKey = generateFileKey(convertedFile);
      originalFiles.value.set(convertedFileKey, convertedFile);
    } catch (err) {
      console.error("Failed to convert HEIC before cropping:", err);
      // Показываем ошибку пользователю
      alert("Не удалось конвертировать HEIC изображение. Попробуйте использовать формат JPEG или PNG.");
      return;
    }
  }

  currentCropFile.value = originalFile;

  // Сохраняем текущую позицию скролла для обоих режимов
  scrollPosition.value = window.scrollY;

  if (props.showInlineCropper) {
    showCropperState.value = true;
    document.body.style.overflow = "hidden";
  } else {
    nextTick(() => {
      setTimeout(() => {
        openModal(cropperModalId);
      }, 50);
    });
  }
};

const closeCropper = () => {
  currentCropFile.value = null;
  const savedPosition = scrollPosition.value;

  if (props.showInlineCropper) {
    showCropperState.value = false;
    document.body.style.overflow = "";
  } else {
    closeModal(cropperModalId);
  }

  // Восстанавливаем позицию скролла для обоих режимов
  requestAnimationFrame(() => {
    window.scrollTo(0, savedPosition);
    scrollPosition.value = 0;
  });
};

const handleCropResult = (result: CropResult) => {
  if (!currentCropFile.value || !files.value) {
    return;
  }

  // Находим индекс файла для замены
  const fileArray = [...files.value];
  let fileIndex = -1;
  const originalName = currentCropFile.value.name;

  // Ищем файл по оригинальному имени или по имени с префиксом cropped-
  for (let i = 0; i < fileArray.length; i++) {
    const currentFile = fileArray[i];
    const currentOriginalName = getOriginalFileName(currentFile.name);

    if (currentOriginalName === originalName) {
      fileIndex = i;
      break;
    }
  }

  if (fileIndex !== -1) {
    // Заменяем файл на обрезанный
    fileArray[fileIndex] = result.file;
    const fileList = createFileList(fileArray);
    updateFileInputValue(fileList);
  }

  emits("crop", currentCropFile.value, result);
  closeCropper();
};

const handleCropError = (error: string) => {
  emits("crop-error", error);
  closeCropper();
};

// Функция сброса к оригинальному файлу
const resetToOriginal = () => {
  if (!currentCropFile.value || !files.value) {
    return;
  }

  // Находим индекс файла для замены
  const fileArray = [...files.value];
  let fileIndex = -1;
  const originalName = currentCropFile.value.name;

  // Ищем файл, который нужно заменить на оригинальный
  for (let i = 0; i < fileArray.length; i++) {
    const currentFile = fileArray[i];
    const currentOriginalName = getOriginalFileName(currentFile.name);

    if (currentOriginalName === originalName) {
      fileIndex = i;
      break;
    }
  }

  if (fileIndex !== -1) {
    // Заменяем файл на оригинальный
    fileArray[fileIndex] = currentCropFile.value;
    const fileList = createFileList(fileArray);
    updateFileInputValue(fileList);
  }
};

// Инициализация при монтировании - сохраняем существующие файлы как оригинальные
watchEffect(() => {
  if (inputFileElement.value) {
    files.value = modelValue.value ?? null;
    inputFileElement.value.files = modelValue.value ?? null;

    // Сохраняем существующие файлы как оригинальные при первой загрузке
    if (modelValue.value && modelValue.value.length > 0) {
      [...modelValue.value].forEach((file) => {
        const originalName = getOriginalFileName(file.name);
        const fileKey = generateFileKey(file);

        // Проверяем, есть ли уже оригинал для этого файла
        let hasOriginal = false;
        for (const [key, origFile] of originalFiles.value.entries()) {
          if (origFile.name === originalName) {
            hasOriginal = true;
            break;
          }
        }

        // Если оригинала нет, сохраняем текущий файл как оригинал
        if (!hasOriginal) {
          // Если файл обрезанный, создаем копию с оригинальным именем
          if (file.name.startsWith("cropped-")) {
            const originalFile = new File([file], originalName, { type: file.type });
            const originalKey = generateFileKey(originalFile);
            originalFiles.value.set(originalKey, originalFile);
          } else {
            originalFiles.value.set(fileKey, file);
          }
        }
      });
    }
  }
});

// ... остальной код для touch events остается без изменений ...

const isDragging = ref(false);
const draggedItem = ref<HTMLElement | null>(null);
const touchStartPosition = ref({ x: 0, y: 0 });
const isTouchDevice = ref(false);

onMounted(() => {
  isTouchDevice.value = "ontouchstart" in window || navigator.maxTouchPoints > 0;

  if (isTouchDevice.value) {
    const draggableElement = document.querySelector(".base-input-file__draggable");

    if (draggableElement) {
      draggableElement.addEventListener("contextmenu", (e) => {
        if (isDragging.value) {
          e.preventDefault();
        }
      });
    }
  }
});

const handleTouchStart = (e: TouchEvent, item: EventTarget | null) => {
  if (!props.draggable || !item) return;

  const htmlItem = item as HTMLElement;
  const touch = e.touches[0];
  if (!touch) return;

  touchStartPosition.value = {
    x: touch.clientX,
    y: touch.clientY,
  };

  setTimeout(() => {
    if (e.touches.length > 0) {
      isDragging.value = true;
      draggedItem.value = htmlItem;
      htmlItem.classList.add("sortable-chosen");
      e.preventDefault();
    }
  }, 200);
};

const handleTouchMove = (e: TouchEvent) => {
  if (!isDragging.value || !draggedItem.value) return;

  e.preventDefault();

  const touch = e.touches[0];
  if (!touch) return;

  const currentPosition = {
    x: touch.clientX,
    y: touch.clientY,
  };

  if (draggedItem.value) {
    draggedItem.value.style.transform = `translate(0, ${currentPosition.y - touchStartPosition.value.y}px)`;
  }

  if (typeof document.elementsFromPoint === "function") {
    const elementsBelow = document.elementsFromPoint(touch.clientX, touch.clientY);

    const droppableElement = elementsBelow.find(
      (element) =>
        element &&
        element.classList &&
        element.classList.contains("base-input-file__list-item") &&
        element !== draggedItem.value,
    );

    if (droppableElement && files.value) {
      const items = [...document.querySelectorAll(".base-input-file__list-item")];
      const draggedIndex = items.indexOf(draggedItem.value);
      const droppedIndex = items.indexOf(droppableElement as HTMLElement);

      if (draggedIndex !== -1 && droppedIndex !== -1) {
        try {
          const fileArray = [...files.value];

          if (fileArray[draggedIndex] && fileArray[droppedIndex]) {
            const temporary = fileArray[draggedIndex];
            fileArray[draggedIndex] = fileArray[droppedIndex];
            fileArray[droppedIndex] = temporary;

            const fileList = createFileList(fileArray);
            updateFileInputValue(fileList);
          }
        } catch (error) {
          console.error("Error during drag operation:", error);
        }
      }
    }
  }
};

const handleTouchEnd = () => {
  if (!isDragging.value) return;

  isDragging.value = false;

  if (draggedItem.value) {
    draggedItem.value.style.transform = "";
    draggedItem.value.classList.remove("sortable-chosen");
    draggedItem.value = null;
  }
};

// Очистка при размонтировании
onUnmounted(() => {
  document.body.style.overflow = "";
});

defineExpose({
  handleClear,
  handleTouchEnd,
  handleTouchMove,
  handleTouchStart,
  inputFileElement,
  isTouchDevice,
  openCropper,
  closeCropper,
  resetToOriginal,
});
</script>

<style scoped>
.base-input-file__outer {
  display: flex;
  flex-direction: column;
}

.base-input-file__wrapper {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.base-input-file__label {
  display: flex;
}

.base-input-file__list {
  position: relative;
  display: flex;
  width: 100%;
}

.base-input-file__draggable {
  display: contents;
}

.base-input-file__inner {
  position: relative;
  display: flex;
  flex-shrink: 0;
  max-width: 100%;
}

.base-input-file__thumb {
  flex-shrink: 0;
}

.base-input-file__element {
  position: absolute;
  inset: 0;
  opacity: 0%;
  cursor: pointer;
}

.base-input-file.loading .base-input-file__element {
  cursor: default;
  pointer-events: none;
}

.base-input-file__loading {
  display: flex;
}

.base-input-file__list-item {
  display: flex;
}

.base-input-file__list-item.draggable {
  cursor: grab;
}

.base-input-file__list-item.sortable-chosen {
  cursor: grabbing;
}

.base-input-file__list-image {
  display: flex;
  object-fit: cover;
  object-position: center;
}

.base-input-file__messages {
  display: flex;
  flex-direction: column;
}

/* Add to <style> section */
.base-input-file__list-item.sortable-chosen {
  opacity: 0.8;
  z-index: 10;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
}

/* Cropper Overlay Styles */
.base-input-file__cropper-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 20px;
}

.base-input-file__cropper-container {
  background: white;
  border-radius: 12px;
  padding: 0;
  max-width: 500px;
  width: 100%;
  max-height: 100vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
}

.base-input-file__cropper-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid var(--grey-6, #f0f0f0);
  background: white;
}

.base-input-file__cropper-title {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: var(--grey-1, #333);
}

.base-input-file__cropper-close {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  padding: 4px 8px;
  color: var(--grey-3, #999);
  border-radius: 4px;
  transition: all 0.2s;
}

.base-input-file__cropper-close:hover {
  background: var(--grey-6, #f0f0f0);
  color: var(--grey-1, #333);
}

.base-input-file__cropper-content {
  flex: 1;
  min-height: 400px;
  overflow: hidden;
  padding: 20px;
}

.base-input-file__cropper-actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  padding: 20px 24px;
  border-top: 1px solid var(--grey-6, #f0f0f0);
  background: white;
}

@media (hover: none) {
  .base-input-file__list-item.draggable {
    touch-action: none;
  }

  .base-input-file__list-item.sortable-chosen {
    transition: transform 0.1s;
  }
}

@media (max-width: 768px) {
  .base-input-file__cropper-overlay {
    padding: 10px;
  }

  .base-input-file__cropper-container {
    max-width: 95vw;
    max-height: 100vh;
  }

  .base-input-file__cropper-header,
  .base-input-file__cropper-actions {
    padding: 16px 20px;
  }

  .base-input-file__cropper-actions {
    flex-direction: column;
  }

  .base-input-file__cropper-content {
    min-height: 300px;
    max-height: 100vh;
  }
}

/* Модификатор для квадратного контейнера 250x250 */
.base-input-file.base-input-file-square {
  max-width: 300px;
  width: 100%;
}

/* Ограничиваем действие стилей только на элементы внутри квадратного контейнера */
.base-input-file.base-input-file-square > .base-input-file__wrapper {
  width: 250px;
  max-height: 280px;
}

.base-input-file.base-input-file-square > .base-input-file__wrapper > .base-input-file__list {
  width: 250px;
  height: 250px;
}

.base-input-file.base-input-file-square .base-input-file__inner {
  width: 250px;
  height: 250px;
}

.base-input-file.base-input-file-square .base-input-file__placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  min-height: 250px;
  text-align: center;
  padding: 20px;
  box-sizing: border-box;
}

.base-input-file.base-input-file-square .base-input-file__list-image {
  width: 250px;
  height: 250px;
  max-width: 250px;
  max-height: 250px;
  object-fit: cover;
  border-radius: 8px;
}

.base-input-file.base-input-file-square .base-input-file__list-item {
  width: 250px;
  height: 250px;
  min-width: 250px;
}
</style>
